// AdminAccountsPage.jsx — C6 (SB-031). Admin-only member management (a traditional
// UI, OQ-B8; NOT the chat console). Backed by /api/admin/* (is_admin gated):
//   GET  /api/admin/job-posts            → the job-post picker options
//   GET  /api/admin/accounts             → the member list
//   POST /api/admin/accounts             → create (admin-precreated; activates on first code login, B4)
//   POST /api/admin/accounts/<id>        → update job post / display name / status (B15/B16)
//   POST /api/admin/accounts/<id>/reset-password → clear password ⇒ force code login (B3)
//
// is_admin is config-authoritative (B11) — NOT editable here; config-admin rows are
// read-only (the backend refuses them too). "Delete" = soft disable (OQ-B16).
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api, { ApiError } from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import { fmtTime } from '../components/TicketDetailView.jsx';
import { useLang, useT } from '../i18n/index.mjs';
import AdminSubnav from '../components/AdminSubnav.jsx';
import DataTable from '../components/DataTable.jsx';
import HelpLink from '../components/HelpLink.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { groupLabel, labelGroupIds } from '../api/groups.mjs';
import { groupFormState, suggestSlug } from './groupCreate.mjs';
import { relativeSince, lastSeenViaKey } from './lastSeen.mjs';
import { summaryOf } from './reportSettings.mjs';
// 第三百一十三批：资方简报设置对话框抽成共享件（/admin/settings 顶部也挂同一只）。
import { ReportSettingsDialog } from '../components/ReportSettingsPanel.jsx';

// 契约 §4.4 写的是裸数组，本仓其他 admin 端点一律套一层（{accounts:[…]}）——两种都收。
// 提到模块级是因为本文件现在有**两个**读组列表的地方（账号表的组编辑器、下方的
// 追踪开关面板）：同一条容错写两遍就是两处会各自漂的解析。
function groupsOf(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.projectGroups)) return data.projectGroups;
  if (Array.isArray(data?.groups)) return data.groups;
  return [];
}

export default function AdminAccountsPage() {
  const t = useT();
  const { lang } = useLang();
  // 第一百零一批（对抗审查修）：建组后刷新会话组上下文——否则管理员刚建的组要等
  // 整页重载才进账号菜单的切换器（/api/me 才是切换器的数据源，不随本页 load 变）。
  const { refresh: refreshAuth } = useAuth();
  const [state, setState] = useState({
    status: 'loading', accounts: [], jobPosts: [], projectGroups: [], error: '',
  });
  const { snack, notify, clearSnack } = useNotify();
  const [busyId, setBusyId] = useState(null);
  // 第一百零二批：正在编辑项目组的那一行（null = 对话框关着）。
  const [groupEdit, setGroupEdit] = useState(null);

  // create form
  const [email, setEmail] = useState('');
  const [jobPost, setJobPost] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [slug, setSlug] = useState('');
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState('');

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      // 第一百零二批：项目组列表随账号一起取——它是"项目组"列的取名表与编辑器的
      // 选项源。三发并行而不是串行：它们互不依赖，串起来只是把首屏多等一个 RTT。
      const [accts, jps, pgs] = await Promise.all([
        api.adminListAccounts(), api.adminJobPosts(), api.adminProjectGroups(),
      ]);
      // Batch 43: /api/admin/job-posts now returns {key, nameEn} objects (the
      // EN UI must not show raw zh keys). Tolerate the old bare-string shape.
      const jobPostList = (Array.isArray(jps?.jobPosts) ? jps.jobPosts : []).map((jp) =>
        typeof jp === 'string' ? { key: jp, nameEn: jp } : jp,
      );
      setState({
        status: 'ready',
        accounts: Array.isArray(accts?.accounts) ? accts.accounts : [],
        jobPosts: jobPostList,
        projectGroups: groupsOf(pgs),
        error: '',
      });
      setJobPost((prev) => prev || jobPostList[0]?.key || '');
    } catch (e) {
      setState({ status: 'error', accounts: [], jobPosts: [], projectGroups: [], error: errMsg(e, t) });
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const onCreate = async (e) => {
    e.preventDefault();
    setCreateErr('');
    if (!email.trim() || !jobPost) {
      setCreateErr(t('admin.accounts.createIncomplete'));
      return;
    }
    if (!email.includes('@')) {
      setCreateErr(t('admin.accounts.badEmail'));
      return;
    }
    setCreating(true);
    try {
      await api.adminCreateAccount({
        email: email.trim(),
        jobPostKey: jobPost,
        displayName: displayName.trim(),
        slug: slug.trim(),
      });
      setEmail('');
      setDisplayName('');
      setSlug('');
      notify(t('admin.accounts.created'), 'success');
      await load();
    } catch (e2) {
      // A slug clash must not be reported as an email clash — it sends the admin
      // to fix the wrong field (SB-033 audit).
      setCreateErr(
        e2 instanceof ApiError && e2.code === 'SLUG_TAKEN' ? t('admin.accounts.slugTaken')
          : e2 instanceof ApiError && e2.status === 409 ? t('admin.accounts.emailExists')
          : errMsg(e2, t),
      );
    } finally {
      setCreating(false);
    }
  };

  const patch = async (acct, body, okMsg) => {
    setBusyId(acct.id);
    try {
      await api.adminUpdateAccount(acct.id, body);
      notify(okMsg, 'success');
      await load();
    } catch (e) {
      notify(errMsg(e, t), 'error');
    } finally {
      setBusyId(null);
    }
  };

  // ── 第一百零二批：成员×组保存 ────────────────────────────────────────────
  // 端点是**替换语义**，所以传的必须是对话框里那一整份集合，不是差量。
  // 保存后除了本页 load()，还要 refreshAuth()——理由与下方建组回调那条逐字相同：
  // 账号菜单里的切换器读的是 /api/me，不随本页 load 变。这里它尤其重要，因为管理员
  // 完全可能编辑的就是**自己那一行**（给自己加一个组），不刷新的话新组要等整页
  // 重载才进得了切换器。
  const saveGroups = async (acct, ids) => {
    setBusyId(acct.id);
    try {
      await api.adminSetAccountGroups(acct.id, ids);
      notify(t('admin.accounts.groups.saved'), 'success');
      setGroupEdit(null);
      await load();
      await refreshAuth();
    } catch (e) {
      notify(errMsg(e, t), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const onClearQuota = async (acct) => {
    setBusyId(acct.id);
    try {
      await api.adminClearCodeQuota(acct.id);
      notify(t('admin.accounts.quotaCleared'), 'success');
    } catch (e) {
      notify(errMsg(e, t), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const onResetPassword = async (acct) => {
    setBusyId(acct.id);
    try {
      await api.adminResetPassword(acct.id);
      notify(t('admin.accounts.passwordCleared'), 'success');
      await load();
    } catch (e) {
      notify(errMsg(e, t), 'error');
    } finally {
      setBusyId(null);
    }
  };

  // 第一百零七批：「最后活动」的相对时间基准。**每次渲染取一次**，不进 state：
  // 这一列的量级是分钟/小时/天（后端本身按 5 分钟节流记录），为它挂一个每秒
  // 重算的定时器只会让整张表每秒重渲一次，换来一个没有人看得出差别的刷新。
  const nowMs = Date.now();

  return (
    <Box>
      <AdminSubnav />
      {/* 第八十八批：删掉标题行右侧那条通往 /admin/git 的互跳链接。
          它是 `AdminSubnav` 之前的遗留物——子页签已经把全部六个管理页列在正上方，
          于是同一个跳转在同一屏上出现两次，而第二次还带着一套只有它自己用的文案键。
          冗余入口不是"多一条路"，是**多一份要跟着改的事实**（这次改名就撞上了）。 */}
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t('admin.accounts.title')}
        <HelpLink section="admin" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('admin.accounts.subtitle')}
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>{t('admin.accounts.slug.edit')}</Alert>

      {/* ── create member ─────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            {t('admin.accounts.addTitle')}
          </Typography>
          <Box component="form" onSubmit={onCreate} noValidate>
            <Stack sx={{ alignItems: { md: 'flex-start' } }} direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label={t('admin.accounts.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={creating}
                fullWidth
                type="email"
              />
              <TextField
                select
                label={t('admin.accounts.jobPost')}
                value={jobPost}
                onChange={(e) => setJobPost(e.target.value)}
                disabled={creating || state.jobPosts.length === 0}
                sx={{ minWidth: { md: 180 }, width: { xs: '100%', md: 'auto' } }}
              >
                {state.jobPosts.map((jp) => (
                  <MenuItem key={jp.key} value={jp.key}>
                    {lang === 'en' ? jp.nameEn : jp.key}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('admin.accounts.displayName')}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={creating}
                fullWidth
                slotProps={{ htmlInput: { maxLength: 80 } }}
              />
              <TextField
                label={t('admin.accounts.slug')}
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                disabled={creating}
                sx={{ minWidth: { md: 180 }, width: { xs: '100%', md: 'auto' } }}
                slotProps={{ htmlInput: { maxLength: 40 } }}
                helperText={t('admin.accounts.slug.helper')}
              />
              <Button type="submit" variant="contained" disabled={creating} sx={{ whiteSpace: 'nowrap', py: 1.75 }}>
                {t('admin.accounts.add')}
              </Button>
            </Stack>
            {createErr ? <Alert severity="error" sx={{ mt: 2 }}>{createErr}</Alert> : null}
          </Box>
        </CardContent>
      </Card>

      {/* ── member list ───────────────────────────────────────────────── */}
      {state.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : state.status === 'error' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>{state.error}</Alert>
          <Button size="small" onClick={load}>{t('common.retry')}</Button>
        </Box>
      ) : (
        // 七列被 360px 视口压到最窄 46px（第九十一批实测）——minWidth 让滚动容器真正接管。
        // 第一百零二批加"项目组"列 ⇒ 八列，按 DataTable 头注的取值参考（七列≈900、
        // 约 130px/列）抬到 1040；这张表本来就是明确要滚的那一类，不受平板免滚线约束。
        // 第一百零七批加"最后活动"列 ⇒ 九列，同一口径 +130 ⇒ 1170。
        <DataTable minWidth={1170}>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.accounts.email')}</TableCell>
                <TableCell>{t('admin.accounts.slug')}</TableCell>
                <TableCell>{t('admin.accounts.displayName')}</TableCell>
                <TableCell>{t('admin.accounts.jobPost')}</TableCell>
                {/* 第一百零二批（多项目制 Slice B）：这个账号属于哪些项目组。
                    它决定该账号在成员面（工作项/工时/汇报）看得见谁的数据，
                    此前只能靠 CLI 或直接改库。 */}
                <TableCell>{t('admin.accounts.groups')}</TableCell>
                {/* 第八十六批：这个账号代表几个人。外包账号代表一支队伍时，
                    R-11 的日上限是 16h x 人数——不显示它，工时表上那个数就无从解释。 */}
                <TableCell align="right">{t('admin.accounts.headcount')}</TableCell>
                <TableCell>{t('admin.accounts.status')}</TableCell>
                {/* 第一百零七批：这个账号最近一次**真的用了**系统是什么时候。
                    需求方裁：含经 API key 的操作——一个只被 agent 用着的账号与一个
                    没人碰过的账号，在"最后登录"那一列上长得一模一样。
                    表头带 title：这一列的口径（含 key、5 分钟量级）解释不清的话，
                    读的人会把它当成"最后登录"来用。 */}
                <TableCell title={t('admin.accounts.lastSeen.hint')}>
                  {t('admin.accounts.lastSeen')}
                </TableCell>
                <TableCell align="right">{t('admin.accounts.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.accounts.map((a) => {
                const locked = a.isAdmin; // config-admin rows are read-only (B11)
                const busy = busyId === a.id;
                return (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      {a.email}
                      {a.isAdmin ? (
                        <Chip size="small" color="secondary" label={t('shell.account.admin')} sx={{ ml: 1 }} />
                      ) : null}
                      {!a.activatedAtMs ? (
                        <Chip size="small" variant="outlined" label={t('admin.accounts.pending')} sx={{ ml: 1 }} />
                      ) : null}
                    </TableCell>
                    {/* SB-033: editable on purpose. The startup backfill declines to
                        guess on a collision or an illegal shape and leaves the key
                        blank — "the admin fixes it" has to be a real path. */}
                    <TableCell sx={{ minWidth: 140 }}>
                      <SlugCell
                        acct={a}
                        disabled={locked || busy}
                        onSave={(next) => patch(a, { slug: next }, t('admin.accounts.updated'))}
                      />
                    </TableCell>
                    <TableCell>{a.displayName || '—'}</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <TextField
                        select
                        size="small"
                        value={a.jobPostKey}
                        disabled={locked || busy || state.jobPosts.length === 0}
                        onChange={(e) =>
                          patch(a, { jobPostKey: e.target.value }, t('admin.accounts.updated'))
                        }
                        variant="standard"
                        sx={{ minWidth: 120 }}
                      >
                        {(state.jobPosts.some((jp) => jp.key === a.jobPostKey)
                          ? state.jobPosts
                          : [{ key: a.jobPostKey, nameEn: a.jobPostNameEn || a.jobPostKey },
                             ...state.jobPosts]
                        ).map((jp) => (
                          <MenuItem key={jp.key} value={jp.key}>
                            {lang === 'en' ? jp.nameEn : jp.key}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    {/* ── 项目组（第一百零二批）───────────────────────────
                        **刻意不受 `locked` 约束**。config 管理员行的只读约束针对的是
                        "管理员身份"（B11：is_admin 由 config 权威，页面改不得），而
                        项目组是另一回事：管理员本来就全组可见（/api/me 给管理员回的是
                        全部未归档组），编辑他的成员行不会给他任何新的可见性，也不会
                        收走任何东西——它只影响"他属于哪个组"这件事实本身。
                        下一个读者看到这里没有 disabled={locked} 时，请读这一段，
                        而不是以为约束漏了。 */}
                    <TableCell sx={{ minWidth: 200 }}>
                      <GroupsCell
                        acct={a}
                        groups={state.projectGroups}
                        lang={lang}
                        t={t}
                        busy={busy}
                        onEdit={() => setGroupEdit(a)}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 96 }}>
                      {/* 就地改，不进弹窗：这是个一年动两次的数，为它开一个对话框
                          的代价比它本身大。失焦即提交；值没变就不发请求——一次
                          什么都不改的 PATCH 会在审计里留下一条"账号变更"，
                          而那条记录说的不是真的。 */}
                      <TextField
                        type="number"
                        size="small"
                        variant="standard"
                        defaultValue={a.headcount ?? 1}
                        disabled={locked || busy}
                        slotProps={{ htmlInput: { min: 1, style: { textAlign: 'right', width: 56 } } }}
                        onBlur={(e) => {
                          const n = Number(e.target.value);
                          if (!Number.isInteger(n) || n < 1) {
                            e.target.value = String(a.headcount ?? 1);   // 非法值弹回
                            return;
                          }
                          if (n === (a.headcount ?? 1)) return;          // 没变就不发
                          patch(a, { headcount: n }, t('admin.accounts.updated'));
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={a.status === 'active' ? 'success' : 'default'}
                        label={t(`admin.accounts.status.${a.status}`) ===
                          `admin.accounts.status.${a.status}`
                          ? a.status
                          : t(`admin.accounts.status.${a.status}`)}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <LastSeenCell acct={a} nowMs={nowMs} lang={lang} t={t} />
                    </TableCell>
                    <TableCell align="right">
                      {locked ? (
                        <Typography variant="caption" color="text.secondary">
                          {t('admin.accounts.configManaged')}
                        </Typography>
                      ) : (
                        <Stack sx={{ justifyContent: 'flex-end', flexWrap: 'wrap' }} direction="row" spacing={0.5}>
                          <Button
                            size="small"
                            disabled={busy}
                            onClick={() =>
                              patch(
                                a,
                                { status: a.status === 'active' ? 'disabled' : 'active' },
                                t('admin.accounts.updated'),
                              )
                            }
                          >
                            {a.status === 'active' ? t('admin.accounts.disable') : t('admin.accounts.enable')}
                          </Button>
                          <Button size="small" disabled={busy} onClick={() => onResetPassword(a)}>
                            {t('admin.accounts.resetPassword')}
                          </Button>
                          {/* SB-037/F4: the daily send cap is keyed on the email and
                              request-code needs no session, so anyone who knows the
                              address can exhaust it. This is the release valve. */}
                          <Button size="small" disabled={busy} onClick={() => onClearQuota(a)}>
                            {t('admin.accounts.clearQuota')}
                          </Button>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
        </DataTable>
      )}

      {/* 组编辑对话框。**开着才挂载**（不是 open={Boolean(...)} 常驻）：初始勾选集
          由 acct.projectGroupIds 派生，常驻的话换一行再打开会带着上一行的勾选——
          与其加一条同步 effect，不如让"新的一行＝新的组件实例"。 */}
      {groupEdit ? (
        <AccountGroupsDialog
          acct={groupEdit}
          groups={state.projectGroups}
          lang={lang}
          t={t}
          busy={busyId === groupEdit.id}
          onClose={() => setGroupEdit(null)}
          onSave={(ids) => saveGroups(groupEdit, ids)}
        />
      ) : null}

      {/* ── 项目组与文档改动追踪（第五十五批 §5.4） ─────────────────────── */}
      {/* refreshAuth 从这里往下传：它此前被 ProjectGroupPanel 直接引用，而那是
          **父组件的局部变量**——建组成功后的回调一跑就是 ReferenceError，于是"建了组
          却提示失败、切换器也不更新"。作用域穿透没有任何构建期判据看得住（esbuild
          不做未定义标识符检查），只能靠显式传参把它变成结构。 */}
      <ProjectGroupPanel t={t} lang={lang} notify={notify}
                         refreshAuth={refreshAuth} onGroupsChanged={load} />

      <AppSnackbar snack={snack} onClose={clearSnack} />
    </Box>
  );
}

// ── 最后活动：账号表里的读面（第一百零七批）──────────────────────────────────
// 相对时间 + 凭据类型徽标。取整与"从未"的判定全在 lastSeen.mjs（纯函数，有单测），
// 这里只负责显示。
//
// 三条刻意之处：
//   ① **从未活动显示「—」**，不是 1970 年、也不是"无"这类可能被读成状态的词；
//   ② 绝对时间放在 title 里 ——「3 天前」回答的是"还在不在用"，而排查具体某一次
//      访问时需要的是准确时刻，两者都要有，但只有一个该占版面；
//   ③ 徽标标注这一次是**会话**还是 **key**：一个只有 agent 在用的账号与一个人在用
//      的账号，参与情况完全不同，而混成一个时间戳就把这个区分丢了。
function LastSeenCell({ acct, nowMs, lang, t }) {
  const rel = relativeSince(acct.lastSeenAtMs, nowMs);
  if (!rel) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const viaKey = lastSeenViaKey(acct.lastSeenVia);
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
      <Typography variant="body2" title={fmtTime(acct.lastSeenAtMs, lang)}>
        {t(rel.key, rel.vars)}
      </Typography>
      {viaKey ? (
        <Chip size="small" variant="outlined" label={t(viaKey)} sx={{ height: 20 }} />
      ) : null}
    </Stack>
  );
}

// ── 成员×组：账号表里的读面（第一百零二批）──────────────────────────────────
// 多组逗号分隔。分隔符按界面语言取：中文用顿号，英文用逗号+空格——同一个字符在
// 两种语言里读起来不是一回事，而这一列很可能同时装着中英两种组名。
function GroupsCell({ acct, groups, lang, t, busy, onEdit }) {
  const names = labelGroupIds(acct.projectGroupIds, groups, lang);
  return (
    <Stack spacing={0.25} sx={{ alignItems: 'flex-start' }}>
      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
        {names.length ? names.join(lang === 'en' ? ', ' : '、')
          : <Box component="span" sx={{ color: 'text.secondary' }}>{t('admin.accounts.groups.none')}</Box>}
      </Typography>
      <Button size="small" disabled={busy} onClick={onEdit} sx={{ minWidth: 0, px: 0.5 }}>
        {t('admin.accounts.groups.edit')}
      </Button>
    </Stack>
  );
}

// ── 成员×组：编辑对话框（第一百零二批）──────────────────────────────────────
// 端点是替换语义，所以这里的勾选集**就是**保存后的全部成员关系——UI 因此必须一次
// 呈现全部可选组并带出当前状态，而不是做成"加入/移除"两个动作（那种形态会让人以为
// 没勾的组保持不变）。清空全部是合法输入，故保存按钮不设"至少一个"的门。
function AccountGroupsDialog({ acct, groups, lang, t, busy, onClose, onSave }) {
  const active = new Set(groups.map((g) => String(g.id)));
  // 已归档（或已消失）的组：关系表里还有行，但后端逐 id 校验 active，原样回传会被
  // 400 RULE_VIOLATION 顶回来——那会让这一行**永远存不了**，而管理员看不出为什么。
  // 所以把它们从初始勾选里摘掉，并在对话框里明说"这次保存会一并移除"：替换语义下
  // 这是实话，静默保留或静默丢弃都不是。
  const current = (Array.isArray(acct.projectGroupIds) ? acct.projectGroupIds : []).map(String);
  const dropped = current.filter((id) => !active.has(id));
  const [ids, setIds] = useState(() => current.filter((id) => active.has(id)));
  const toggle = (id) =>
    setIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Dialog open fullWidth maxWidth="xs" onClose={() => (busy ? null : onClose())}>
      <DialogTitle>{t('admin.accounts.groups.title', { email: acct.email })}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('admin.accounts.groups.hint')}
        </Typography>
        {acct.isAdmin ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>{t('admin.accounts.groups.adminNote')}</Alert>
        ) : null}
        {dropped.length ? (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {t('admin.accounts.groups.dropped', { list: dropped.map((id) => `#${id}`).join(', ') })}
          </Alert>
        ) : null}
        {groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('admin.accounts.groups.empty')}
          </Typography>
        ) : (
          <FormGroup>
            {groups.map((g) => (
              <FormControlLabel
                key={g.id}
                control={
                  <Checkbox
                    checked={ids.includes(String(g.id))}
                    disabled={busy}
                    onChange={() => toggle(String(g.id))}
                  />
                }
                label={groupLabel(g, lang) || g.slug}
              />
            ))}
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>{t('common.cancel')}</Button>
        {/* Number 化在**发送前**一次做完：后端要整数，勾选态用字符串是为了 id 类型
            不敏感地比对（/api/me 与 admin 两只端点的 id 都是数字，但这份集合还要
            和上面那份 String 化的 current 对齐）。 */}
        <Button variant="contained" disabled={busy} onClick={() => onSave(ids.map(Number))}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── 项目组：文档改动追踪的**唯一**操作面（第五十五批 §5.4） ──────────────────
// 开关只入库、只在这里操作，config 里不设：config 烤进镜像，重新部署会用文件值
// 覆盖回库里的状态，等于把一个不可逆的闸静默解锁——"不可逆"与"声明式配置每次
// 部署重施"在语义上互斥（需求方已裁）。
// 因此这里只有"开"没有"关"：关闭动作在后端也会被 400 IRREVERSIBLE 拒绝。
function ProjectGroupPanel({ t, lang, notify, refreshAuth, onGroupsChanged }) {
  const [state, setState] = useState({ status: 'loading', groups: [], error: '' });
  const [confirm, setConfirm] = useState(null);   // 待确认开启的那一组
  const [report, setReport] = useState(null);     // SB-202 S3：正在编辑资方简报设置的那一组
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const data = await api.adminProjectGroups();
      setState({ status: 'ready', groups: groupsOf(data), error: '' });
    } catch (e) {
      setState({ status: 'error', groups: [], error: groupErrMsg(e, t) });
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const doEnable = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await api.adminEnableLogTracking(confirm.id);
      notify(t('admin.groups.enabled'), 'success');
      setConfirm(null);
      await load();
    } catch (e) {
      notify(groupErrMsg(e, t), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          {t('admin.groups.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('admin.groups.subtitle')}
        </Typography>

        {state.status === 'loading' ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : state.status === 'error' ? (
          <Box>
            <Alert severity="error" sx={{ mb: 1 }}>{state.error}</Alert>
            <Button size="small" onClick={load}>{t('common.retry')}</Button>
          </Box>
        ) : state.groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t('admin.groups.empty')}</Typography>
        ) : (
          // ≤640：本表在 Card 的 CardContent 里，768px 下内容盒只剩 ~686px，720 会强出假滚动
          <DataTable minWidth={640}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.groups.slug')}</TableCell>
                  <TableCell>{t('admin.groups.name')}</TableCell>
                  <TableCell>{t('admin.groups.tracking')}</TableCell>
                  <TableCell>{t('admin.groups.origin')}</TableCell>
                  <TableCell>{t('admin.groups.report')}</TableCell>
                  <TableCell align="right">{t('admin.accounts.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.groups.map((g) => (
                  <TableRow key={g.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{g.slug}</TableCell>
                    <TableCell>
                      {/* 两个名字都显示：管理页要能一眼看出两列都填了，
                          而不是等英文用户看到一个中文名才发现。 */}
                      <Stack spacing={0.25}>
                        <Typography variant="body2">
                          {(lang === 'en' ? g.displayNameEn : g.displayName) || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(lang === 'en' ? g.displayName : g.displayNameEn) || '—'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={g.logTrackingEnabled ? 'success' : 'default'}
                        label={g.logTrackingEnabled ? t('admin.groups.on') : t('admin.groups.off')}
                      />
                    </TableCell>
                    <TableCell>
                      {g.logTrackingEnabled ? (
                        <Stack spacing={0.25}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {/* 起算点：40 位 sha 截前 7 位显示；拿不到时退化为按开启时间过滤。 */}
                            {g.logTrackingOriginRef
                              ? String(g.logTrackingOriginRef).slice(0, 7)
                              : t('admin.groups.originUnknown')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {fmtTime(g.logTrackingEnabledAtMs, lang)}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell data-report-cell={g.slug}>
                      {/* SB-202 S3（第三百零八批）：资方简报设置的读面——开／关 · 间隔 · 收件／排除／闸门数。
                          编辑走右侧「设置」；这一格只照 admin GET 投影的 report 字段显示。 */}
                      {(() => {
                        const s = summaryOf(g.report);
                        return (
                          <Stack spacing={0.25}>
                            <Chip size="small" color={s.enabled ? 'success' : 'default'}
                                  label={s.enabled ? t('admin.groups.report.on', { days: s.everyDays }) : t('admin.groups.report.off')} />
                            <Typography variant="caption" color="text.secondary">
                              {t('admin.groups.report.counts', { recipients: s.recipients, exclude: s.exclude, gates: s.gates })}
                            </Typography>
                          </Stack>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button size="small" disabled={busy} onClick={() => setReport(g)} data-report-edit={g.slug}>
                          {t('admin.groups.report.edit')}
                        </Button>
                        {g.logTrackingEnabled ? (
                          <Typography variant="caption" color="text.secondary">
                            {t('admin.groups.irreversibleNote')}
                          </Typography>
                        ) : (
                          <Button size="small" disabled={busy} onClick={() => setConfirm(g)}>
                            {t('admin.groups.enable')}
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
          </DataTable>
        )}

        {/* 建组后要刷三处，缺一处就有一个面在说旧话：
              · load()          —— 本面板的组表；
              · onGroupsChanged()—— 上方账号表的"项目组"列与编辑器选项（新组必须
                马上能勾，否则建完还要手动刷新页面才能把人放进去）；
              · refreshAuth()   —— 账号菜单里的切换器（数据源是 /api/me，不随本页变）。*/}
        <CreateGroupForm t={t} notify={notify}
                         onCreated={async () => {
                           await load();
                           await onGroupsChanged();
                           await refreshAuth();
                         }} />

        <Dialog open={Boolean(confirm)} onClose={() => (busy ? null : setConfirm(null))}>
          <DialogTitle>{t('admin.groups.confirmTitle', { slug: confirm?.slug || '' })}</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 1.5 }}>{t('admin.groups.confirmIrreversible')}</Alert>
            <Typography variant="body2">{t('admin.groups.confirmDesc')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => setConfirm(null)}>{t('common.cancel')}</Button>
            <Button color="warning" variant="contained" disabled={busy} onClick={doEnable}>
              {t('admin.groups.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {report ? (
          <ReportSettingsDialog t={t} notify={notify} group={report}
                                onClose={() => setReport(null)}
                                onSaved={async () => { setReport(null); await load(); }} />
        ) : null}
      </CardContent>
    </Card>
  );
}

// ── 第八十五批：运行期建组（需求方裁「项目组应该可以在运行期创建」）──────────
// 在此之前项目组只能在开机时播种，于是 `ensure_friction` 这条能力没有调用点——
// 它本该在"新组诞生"那一刻被调，而新组从来不诞生。
//
// 表单的三条刻意之处：
//   ① **中英两个名字都必填**。只收一个再复制到另一列是播种期的做法，那会把中文名
//      钉进英文列，而目录页照列名渲染（第四十三批语言规则）。
//   ② slug 建成后**不可更改**（它是发布目录的一段），故校验在按下之前就给出，
//      且提示点名是哪条规则——"格式不对"对用户毫无用处。
//   ③ 成功提示里带上**同批建出的内置摩擦单号**：它是"这个组已经完整可用"的凭据。
//      R-29 规定驳回的工作项归入内置摩擦单，而驳回发生在审批那一刻——若那时摩擦单
//      还不存在，失败的是审批人而不是建组的人。
function CreateGroupForm({ t, notify, onCreated }) {
  const [form, setForm] = useState({ slug: '', displayName: '', displayNameEn: '' });
  const [touched, setTouched] = useState({});
  const [busy, setBusy] = useState(false);
  const { problems, canSubmit } = groupFormState(form);

  // 英文名推 slug，但**只在用户还没自己动过 slug 时**——推翻用户的手输是最讨厌的
  // 一种"智能"。中文推不出（没有音译规则可依），故只挂在英文名上。
  const onEnName = (v) => setForm((f) => {
    const next = { ...f, displayNameEn: v };
    if (!touched.slug) next.slug = suggestSlug(v);
    return next;
  });

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.adminCreateProjectGroup(form);
      notify(t('admin.groups.created',
               { slug: r?.slug || form.slug, friction: r?.frictionBacklogNo || '—' }), 'success');
      setForm({ slug: '', displayName: '', displayNameEn: '' });
      setTouched({});
      await onCreated();
    } catch (e) {
      notify(t(errKey(e, { byStatus: { 409: 'admin.groups.slugTaken' },
                           fallback: 'admin.groups.error' })), 'error');
    } finally {
      setBusy(false);
    }
  };

  // 只在**碰过**该字段后才报错：一进页面三行红字，读起来像页面坏了。
  const err = (k, prefix) => (touched[k] && problems[k]
    ? t(`admin.groups.err.${prefix}.${problems[k]}`) : ' ');

  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography sx={{ fontWeight: 700 }} variant="subtitle2">{t('admin.groups.createTitle')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('admin.groups.createHint')}
      </Typography>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            size="small" fullWidth label={t('admin.groups.nameZh')} value={form.displayName}
            onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
            onBlur={() => setTouched((x) => ({ ...x, displayName: true }))}
            error={Boolean(touched.displayName && problems.displayName)}
            helperText={err('displayName', 'name')}
          />
          <TextField
            size="small" fullWidth label={t('admin.groups.nameEn')} value={form.displayNameEn}
            onChange={(e) => onEnName(e.target.value)}
            onBlur={() => setTouched((x) => ({ ...x, displayNameEn: true }))}
            error={Boolean(touched.displayNameEn && problems.displayNameEn)}
            helperText={err('displayNameEn', 'name')}
          />
        </Stack>
        <TextField
          size="small" label={t('admin.groups.slugLabel')} value={form.slug}
          onChange={(e) => {
            setTouched((x) => ({ ...x, slug: true }));
            setForm((f) => ({ ...f, slug: e.target.value }));
          }}
          onBlur={() => setTouched((x) => ({ ...x, slug: true }))}
          error={Boolean(touched.slug && problems.slug)}
          helperText={touched.slug && problems.slug
            ? t(`admin.groups.err.slug.${problems.slug}`)
            : t('admin.groups.slugHelp')}
          slotProps={{ htmlInput: { style: { fontFamily: 'monospace' }, maxLength: 40 } }}
          sx={{ maxWidth: 360 }}
        />
        <Box>
          <Button
            variant="contained" size="small" disabled={!canSubmit || busy}
            onClick={submit}
          >
            {t('admin.groups.create')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

// 单向闸被后端拒绝时（IRREVERSIBLE）说清楚是"不可关闭"，而不是一句通用失败。
function groupErrMsg(e, t) {
  return t(errKey(e, {
    byCode: { IRREVERSIBLE: 'admin.groups.irreversible' },
    byStatus: { 403: 'admin.accounts.forbidden', 404: 'admin.groups.notFound' },
    fallback: 'admin.groups.error',
  }));
}

// Commit-on-blur/Enter so a half-typed key is never sent; Escape reverts.
function SlugCell({ acct, disabled, onSave }) {
  const [draft, setDraft] = useState(acct.slug || '');
  useEffect(() => setDraft(acct.slug || ''), [acct.slug]);
  const commit = () => {
    const next = draft.trim();
    if (next === (acct.slug || '')) return;
    onSave(next);
  };
  return (
    <TextField
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') setDraft(acct.slug || '');
      }}
      disabled={disabled}
      variant="standard"
      size="small"
      placeholder="—"
      slotProps={{ htmlInput: { maxLength: 40, style: { fontFamily: 'monospace' } } }}
      sx={{ minWidth: 110 }}
    />
  );
}

// Page-specific spec for the shared mapper (SB-042). Codes beat statuses (a
// SLUG_TAKEN must never read as an email clash); raw backend English never
// reaches the localized UI (SB-031 review F6).
function errMsg(e, t) {
  return t(errKey(e, {
    byCode: { SLUG_TAKEN: 'admin.accounts.slugTaken' },
    byStatus: {
      403: 'admin.accounts.forbidden',
      404: 'admin.accounts.notFound',
      409: 'admin.accounts.conflictEdit',
      400: 'admin.accounts.badRequest',
    },
    fallback: 'admin.accounts.error',
  }));
}
